import type {
  ImportJob,
  ImportJobErrorSample,
  ImportStatusDetails,
} from '@/services/products/types'

// Helper functions for ImportJob
export const getImportedCount = (job: ImportJob): number =>
  job.successfulItems ?? job.configuration?.importedCount ?? 0

export const getFailedCount = (job: ImportJob): number =>
  job.failedItems ?? job.configuration?.failedCount ?? 0

export const getTotalItems = (job: ImportJob): number => job.totalItems ?? 0

export const getProcessedItems = (job: ImportJob): number =>
  job.processedItems ?? 0

export const getFileName = (job: ImportJob): string => {
  if (job.sourceType === 'url' && job.sourceUrl) {
    try {
      return new URL(job.sourceUrl).hostname
    } catch {
      return 'Website Import'
    }
  }
  return (
    (job as ImportJob & { fileName?: string }).fileName ??
    job.configuration?.originalFilename ??
    'Unknown'
  )
}

export const getErrorMessage = (job: ImportJob): string | undefined =>
  job.errorMessage ?? job.configuration?.error

export const getErrorSamples = (job: ImportJob): ImportJobErrorSample[] =>
  job.errorSamples ?? job.configuration?.errorSamples ?? []

export const isImportSuccessful = (job: ImportJob): boolean =>
  job.status === 'completed' && getImportedCount(job) > 0

export const isImportPartiallySuccessful = (job: ImportJob): boolean =>
  job.status === 'completed_with_errors' ||
  (job.status === 'completed' && getFailedCount(job) > 0)

export const isImportFailed = (job: ImportJob): boolean =>
  job.status === 'failed' ||
  (job.status === 'completed' && getImportedCount(job) === 0)

export const getImportedStatusMessage = (
  job: ImportJob,
  collectionName?: string
): string => {
  const importedCount = getImportedCount(job)
  const failedCount = getFailedCount(job)

  if (job.status === 'failed') {
    const errorMsg = getErrorMessage(job)
    if (errorMsg) {
      return errorMsg
    }
    return `Import failed. ${failedCount} items could not be processed.`
  }

  if (job.status === 'completed_with_errors') {
    const collectionSuffix = collectionName ? ` to "${collectionName}"` : ''
    return `Imported ${importedCount} products${collectionSuffix}, but ${failedCount} items failed.`
  }

  if (job.status === 'completed') {
    if (importedCount === 0 && failedCount > 0) {
      // Edge case: marked as completed but nothing imported
      return `Import failed. All ${failedCount} items could not be processed.`
    }

    const baseMessage = `Successfully imported ${importedCount} products`
    const collectionSuffix = collectionName ? ` to "${collectionName}"` : ''
    const failedSuffix = failedCount > 0 ? ` ${failedCount} items failed.` : ''

    return `${baseMessage}${collectionSuffix}.${failedSuffix}`
  }

  return 'Importing products...'
}

function describeFailed(job: ImportJob): ImportStatusDetails {
  const failedCount = getFailedCount(job)
  const errorSamples = getErrorSamples(job)

  return {
    type: 'error',
    title: 'Import Failed',
    description:
      getErrorMessage(job) ||
      `All ${failedCount} items could not be processed.`,
    errorSamples: errorSamples.length > 0 ? errorSamples : undefined,
  }
}

function describeCompletedWithErrors(job: ImportJob): ImportStatusDetails {
  const importedCount = getImportedCount(job)
  const failedCount = getFailedCount(job)
  const errorSamples = getErrorSamples(job)

  return {
    type: 'warning',
    title: 'Import Completed with Errors',
    description: `${importedCount} products imported successfully, ${failedCount} items failed.`,
    errorSamples: errorSamples.length > 0 ? errorSamples : undefined,
  }
}

function describeCompleted(job: ImportJob): ImportStatusDetails {
  const importedCount = getImportedCount(job)
  const failedCount = getFailedCount(job)

  if (importedCount === 0 && failedCount > 0) {
    const errorSamples = getErrorSamples(job)
    return {
      type: 'error',
      title: 'Import Failed',
      description: `All ${failedCount} items could not be processed.`,
      errorSamples: errorSamples.length > 0 ? errorSamples : undefined,
    }
  }

  return {
    type: 'success',
    title: 'Import Complete',
    description: `Successfully imported ${importedCount} products.`,
  }
}

function describeProcessing(job: ImportJob): ImportStatusDetails {
  const processed = getProcessedItems(job)
  const total = getTotalItems(job)

  return {
    type: 'info',
    title: 'Importing...',
    description:
      total > 0
        ? `Processing ${processed} of ${total} items...`
        : 'Processing...',
  }
}

export const getImportStatusDetails = (job: ImportJob): ImportStatusDetails => {
  switch (job.status) {
    case 'failed':
      return describeFailed(job)
    case 'completed_with_errors':
      return describeCompletedWithErrors(job)
    case 'completed':
      return describeCompleted(job)
    case 'detecting':
      return {
        type: 'info',
        title: 'Analyzing Feed',
        description: 'Reading your feed to find the product fields...',
      }
    case 'mapping_pending':
      return {
        type: 'action',
        title: 'Mapping Needed',
        description:
          'Confirm how your feed fields map to product fields to continue.',
      }
    case 'converting':
      return {
        type: 'info',
        title: 'Converting Feed',
        description: 'Converting your feed into products...',
      }
    case 'processing':
      return describeProcessing(job)
    default:
      return {
        type: 'info',
        title: 'Pending',
        description: 'Import is queued and will start shortly.',
      }
  }
}
